import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { FileText, BarChart, Users2, Video, Globe, ClipboardList, LayoutTemplate, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

interface AnalysisNavProps {
  id: string;
  activeTab?: string;
}

export function AnalysisNav({ id, activeTab }: AnalysisNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  // Determine active tab from path if not explicitly provided
  const determineActiveTab = () => {
    if (activeTab) return activeTab;
    if (location.pathname.includes("/analysis/")) return "analysis";
    if (location.pathname.includes("/valuation/")) return "valuation";
    if (location.pathname.includes("/founders/")) return "team";
    if (location.pathname.includes("/market-research/")) return "research";
    if (location.pathname.includes("/interview/")) return "interview";
    if (location.pathname.includes("/investment-memo/")) return "memo";
    if (location.pathname.includes("/presentation-analysis/")) return "presentation";
    return "analysis";
  };

  const active = determineActiveTab();
  
  const tabs = [
    {
      name: "Pitch Deck Analysis",
      href: `/analysis/${id}`,
      icon: FileText,
      id: "analysis",
      current: active === "analysis"
    },
    {
      name: "Presentation Analysis",
      href: `/presentation-analysis/${id}`,
      icon: LayoutTemplate,
      id: "presentation",
      current: active === "presentation"
    },
    {
      name: "Valuation",
      href: `/valuation/${id}`,
      icon: BarChart,
      id: "valuation",
      current: active === "valuation"
    },
    {
      name: "Team",
      href: `/founders/${id}`,
      icon: Users2,
      id: "team",
      current: active === "team"
    },
    {
      name: "Market Research",
      href: `/market-research/${id}`,
      icon: Globe,
      id: "research",
      current: active === "research"
    },
    {
      name: "Investor Interview",
      href: `/interview/${id}`,
      icon: Video,
      id: "interview",
      current: active === "interview"
    },
    {
      name: "Investment Memo",
      href: `/investment-memo/${id}`,
      icon: ClipboardList,
      id: "memo",
      current: active === "memo"
    }
  ];

  return (
    <div className="fixed left-0 top-[64px] h-[calc(100vh-64px)] w-64 bg-muted p-4 flex flex-col">
      <nav className="flex flex-col gap-2 flex-1" aria-label="Analysis navigation">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <Link
              key={tab.name}
              to={tab.href}
              className={cn(
                "flex items-center rounded-md px-3 py-2 font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                tab.current
                  ? "bg-background text-foreground shadow-sm"
                  : "hover:bg-background/50 hover:text-foreground"
              )}
              style={{ fontSize: '14px', fontFamily: '"Open Sans", sans-serif' }}
            >
              <Icon className="mr-3 h-4 w-4" />
              <span>{tab.name}</span>
            </Link>
          );
        })}
      </nav>
      <Button
        variant="ghost"
        className="justify-start"
        onClick={async () => {
          await logout();
          navigate('/login');
        }}
      >
        <LogOut className="mr-3 h-4 w-4" />
        <span>Logout</span>
      </Button>
    </div>
  );
}
